import { useRef } from "react";
import { events } from "../../models/site";
import { useCarouselPresenter } from "../../presenters/useInteractionPresenters";
import { SmartImage } from "../SmartImage";
import { ExternalLink } from "../pages/ExternalLink";

type EventItem = (typeof events)[number];

function EventSlide({
  item,
  leaving = false,
}: {
  item: EventItem;
  leaving?: boolean;
}) {
  return (
    <div
      className={`event-slide ${leaving ? "is-leaving" : "is-entering"}`}
      aria-hidden={leaving || undefined}
    >
      <SmartImage
        name={item.image}
        alt={leaving ? "" : `${item.title} ${item.en}`}
        sizes="(max-width: 767px) 100vw, 55vw"
      />
      <article className="event-card">
        <p className="event-date">{item.date}</p>
        <h2>
          {item.title}
          <span>{item.en}</span>
        </h2>
        <p>{item.description}</p>
        {leaving ? (
          <span className="text-link">{item.cta} ↗</span>
        ) : (
          <ExternalLink className="text-link" href={item.href}>
            {item.cta} ↗
          </ExternalLink>
        )}
      </article>
    </div>
  );
}

export function EventShowcase() {
  const carousel = useCarouselPresenter(events.length, { autoPlayMs: 6000 });
  const carouselRef = useRef<HTMLDivElement>(null);
  const event = events[carousel.index];
  const previousEvent =
    carousel.previousIndex === null ? null : events[carousel.previousIndex];

  const showEvent = (index: number) => {
    carousel.goTo(index);

    if (!window.matchMedia("(max-width: 767px)").matches) return;

    const behavior = window.matchMedia("(prefers-reduced-motion: reduce)")
      .matches
      ? "auto"
      : "smooth";
    window.requestAnimationFrame(() => {
      carouselRef.current?.scrollIntoView({ behavior, block: "start" });
    });
  };

  return (
    <section
      className="events-section"
      aria-label="活動焦點與全部活動"
      onMouseEnter={carousel.pauseForInteraction}
      onMouseLeave={carousel.resumeAfterInteraction}
      onFocusCapture={carousel.pauseForInteraction}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null))
          carousel.resumeAfterInteraction();
      }}
    >
      <div className="event-carousel" ref={carouselRef}>
        <div
          className="event-stage"
          aria-live={carousel.isAutoPlaying ? "off" : "polite"}
          aria-atomic="true"
        >
          {previousEvent && (
            <EventSlide
              key={`leaving-${carousel.previousIndex}`}
              item={previousEvent}
              leaving
            />
          )}
          <EventSlide key={`entering-${carousel.index}`} item={event} />
        </div>
        <div className="carousel-controls">
          <button
            type="button"
            onClick={carousel.previous}
            aria-label="上一個活動"
          >
            ←
          </button>
          <span>
            {String(carousel.index + 1).padStart(2, "0")} /{" "}
            {String(events.length).padStart(2, "0")}
          </span>
          <button type="button" onClick={carousel.next} aria-label="下一個活動">
            →
          </button>
          <button
            className="autoplay-toggle"
            type="button"
            onClick={carousel.toggleAutoPlay}
            aria-pressed={carousel.userPaused}
            aria-label={carousel.userPaused ? "開始自動輪播" : "暫停自動輪播"}
          >
            {carousel.userPaused ? "PLAY" : "PAUSE"}
          </button>
        </div>
      </div>
      <div className="event-overview-heading">
        <p>AT A GLANCE</p>
        <h2>快速瀏覽所有活動</h2>
      </div>
      <div className="event-overview" aria-label="所有台東活動">
        {events.map((item, index) => (
          <button
            className={`event-preview ${index === carousel.index ? "is-active" : ""}`}
            type="button"
            key={item.image}
            onClick={() => showEvent(index)}
            aria-label={`顯示活動：${item.title}`}
            aria-current={index === carousel.index ? "true" : undefined}
          >
            <SmartImage
              name={item.image}
              alt=""
              sizes="(max-width: 767px) 50vw, (max-width: 1024px) 33vw, 25vw"
            />
            <span className="event-preview-number">
              {String(index + 1).padStart(2, "0")}
            </span>
            <span className="event-preview-copy">
              <span>{item.date}</span>
              <strong>{item.title}</strong>
            </span>
          </button>
        ))}
      </div>
    </section>
  );
}
